import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import AccessStatePage from "components/AccessStatePage";
import AuthContainer from "components/AuthContainer";
import CircularLoading from "components/CircularLoading";
import { RouteAccessState, getRouteAccessState, isProfileRoute } from "constants/access";
import { COMPANY_USER_ROLE_TITLE } from "constants/company";
import { HEADER_HEIGHT } from "constants/global";
import ROUTES, { isPublicAuthRoute } from "constants/routes";
import { NavigationHistoryProvider } from "context/navigationHistoryContext";
import { UserContext } from "context/userContext";
import withErrorBoundary from "hocs/withErrorBoundary";
import { useContext, useMemo, useRef } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import Header from "./Header";
import { MainLayoutContext } from "./context";

const MainLayout = () => {
  const location = useLocation();
  const { me, meLoading, meError, currentCompanyRole, userCompanies, userCompaniesLoading } = useContext(UserContext);
  const isAuthPage = isPublicAuthRoute(location.pathname);
  const routeAccessState = getRouteAccessState(location.pathname, currentCompanyRole);
  const mainBoxRef = useRef<HTMLDivElement>(null);

  const contextValue = useMemo(() => ({ mainBoxRef }), [mainBoxRef]);
  const stubRoleTitle = currentCompanyRole ? COMPANY_USER_ROLE_TITLE[currentCompanyRole] : "Пользователь";

  const renderPrivateContent = () => {
    if (meLoading || userCompaniesLoading) {
      return <CircularLoading />;
    }

    if (!me || meError) {
      return <Outlet />;
    }

    const isProfilePage = isProfileRoute(location.pathname);

    if (!isProfilePage && Array.isArray(userCompanies) && !userCompanies.length) {
      return (
        <Navigate
          to={`/${ROUTES.NO_COMPANY}`}
          replace
        />
      );
    }

    if (!isProfilePage && Array.isArray(userCompanies) && userCompanies.length > 0 && !currentCompanyRole) {
      return (
        <Navigate
          to={`/${ROUTES.SELECT_COMPANY}${location.search}`}
          replace
        />
      );
    }

    if (routeAccessState === RouteAccessState.STUB) {
      return (
        <AccessStatePage
          title='Функционал в разработке'
          description={`Роль «${stubRoleTitle}» пока не поддерживается в интерфейсе.`}
        />
      );
    }

    if (routeAccessState === RouteAccessState.FORBIDDEN) {
      return (
        <AccessStatePage
          title='Доступ ограничен'
          description='У вас нет доступа к этому разделу в текущей компании.'
        />
      );
    }

    return <Outlet />;
  };

  return (
    <NavigationHistoryProvider>
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        {!isAuthPage && <Header />}
        <Box
          ref={mainBoxRef}
          component='main'
          sx={{
            backgroundColor: "#F6F8FF",
            flexGrow: 1,
            height: `calc(100vh - ${isAuthPage ? "0px" : HEADER_HEIGHT})`,
            overflow: "auto",
          }}>
          <MainLayoutContext.Provider value={contextValue}>
            {isAuthPage ? (
              <AuthContainer>
                <Outlet />
              </AuthContainer>
            ) : (
              <Container
                maxWidth='xl'
                sx={{ mt: 2.5, mb: 2.5 }}>
                {renderPrivateContent()}
              </Container>
            )}
          </MainLayoutContext.Provider>
        </Box>
      </Box>
    </NavigationHistoryProvider>
  );
};

export default withErrorBoundary(MainLayout);
